/**
 * The manual-input (blue) cells of MF_BOV_Underwriting_Template.xlsx.
 *
 * Only these are stored. Every other figure in the popup is derived from them by
 * calculate.js, so the screen, the save and the BOV print cannot drift apart.
 *
 * Percentages are whole numbers, as everywhere else in the app (6.5 = 6.5%); bps_change
 * is a percentage too (0.25 = 25 bps).
 */

/** Fields kept on the PROPERTIES record rather than the underwriting row. */
export const PROPERTY_FIELDS = ['name', 'address', 'city', 'state', 'zip', 'year_built', 'lot_size', 'parking_spaces'];

/**
 * Expense lines, in the Marketing sheet's order - the order the MF BOV prints - which every
 * screen follows. (The Pricing and Expenses sheets list Payroll before Contract Services.)
 * `key` is the current column; proforma is `proforma_${key}`.
 */
export const EXPENSE_LINES = [
    // Real Estate Taxes and Management Fee are calculated, not entered.
    {key: 'real_estate_taxes', label: 'Real Estate Taxes', calculated: true},
    {key: 'insurance', label: 'Insurance'},
    {key: 'utilities', label: 'Utilities'},
    {key: 'repairs_maintenance', label: 'Repairs & Maintenance'},
    {key: 'contract_services', label: 'Contract Services'},
    {key: 'payroll', label: 'Payroll'},
    {key: 'management_fee', label: 'Management Fee', calculated: true},
    {key: 'general_administrative', label: 'General & Administrative'},
    {key: 'marketing', label: 'Marketing'},
];

/** The four economic-vacancy lines (Expenses rows 21-24). */
export const ECONOMIC_VACANCY_LINES = [
    {key: 'non_revenue_units', label: 'NRU – Non-Revenue Units'},
    {key: 'loss_to_lease', label: 'LTL – Loss-to-Lease'},
    {key: 'rent_concessions', label: 'RC – Rent Concessions'},
    {key: 'bad_debt', label: 'BD – Bad Debt'},
];

/** Pricing Range columns, in BPS steps from the cap rate (Pricing I..M; J is the cap rate itself). */
export const PRICING_RANGE_STEPS = [-1, 0, 1, 2, 3];

const enteredExpenseKeys = EXPENSE_LINES.filter(line => !line.calculated).map(line => line.key);

/** Numeric underwriting columns. */
export const NUMBER_FIELDS = [
    'current_tax_appraised_value', 'mill_rate', 'new_assessed_value',
    'cap_rate', 'bps_change',
    'down_payment_percent', 'interest_rate', 'amortization',
    'management_fee_percent', 'proforma_management_fee_percent',
    ...enteredExpenseKeys,
    ...enteredExpenseKeys.map(key => `proforma_${key}`),
    'vacancy', 'proforma_vacancy',
    ...ECONOMIC_VACANCY_LINES.map(line => line.key),
    ...ECONOMIC_VACANCY_LINES.map(line => `proforma_${line.key}`),
];

/** Text underwriting columns. */
export const TEXT_FIELDS = ['county', 'google_maps_link', 'cad_link', 'analysis_date', 'expense_notes'];

export const UNIT_MIX_FIELDS = ['floor_plan', 'units', 'avg_sf', 'rent', 'proforma_rent', 'occupied_units'];

export const DEFAULT_FLOOR_PLANS = ['1 Bed/ 1 Bath', '2 Bed/ 2 Bath', '3 Bed/ 2 Bath'];

export const DEFAULT_OTHER_INCOME_CATEGORIES = [
    'RUBS (Utility Reimbursement)',
    'Pet Fees / Pet Rent',
    'Parking',
    'Late / Application / Admin Fees',
    'Laundry / Vending',
    'Storage',
    'Other',
];

export function emptyUnitMixRow(floorPlan = '') {
    return {floor_plan: floorPlan, units: '', avg_sf: '', rent: '', proforma_rent: '', occupied_units: ''};
}

export function emptyOtherIncomeRow(category = '') {
    return {category, amount: ''};
}

/** A blank underwriting. Only the BPS step has a default (25 bps); cap rate and down payment start empty. */
export function emptyInputs() {
    const inputs = {};
    PROPERTY_FIELDS.forEach(key => { inputs[key] = ''; });
    TEXT_FIELDS.forEach(key => { inputs[key] = ''; });
    NUMBER_FIELDS.forEach(key => { inputs[key] = ''; });
    inputs.bps_change = 0.25;
    inputs.unit_mix = DEFAULT_FLOOR_PLANS.map(emptyUnitMixRow);
    inputs.current_other_income = DEFAULT_OTHER_INCOME_CATEGORIES.map(category => emptyOtherIncomeRow(category));
    inputs.proforma_other_income = DEFAULT_OTHER_INCOME_CATEGORIES.map(category => emptyOtherIncomeRow(category));
    return inputs;
}
